import {
    JsonOpcodeStep, StepKind
} from 'builders/builderOpcode/jsonMode/types';
import {
    makeLLMStepTemplate,
    makeRagBasicSearchTemplate,
    makeApiStepTemplate,
    makeMemoryStepTemplate,
    makeChooseStepTemplate,
    makeSubflowStepTemplate,
    makeToolStepTemplate
} from 'builders/builderOpcode/jsonMode/templates';
import {
    CALL_STEP_AUTHORING_ENABLED
} from './featureFlags';

/**
 * Generates auto-incremented step ID based on the current step count.
 */
export function generateStepId(existingSteps: JsonOpcodeStep[]): string {
    let index = existingSteps.length + 1;
    const existingIds = new Set(existingSteps.map(s => s.id));

    // Ensure uniqueness
    while (existingIds.has(`step_${index}`)) {
        index++;
    }

    return `step_${index}`;
}

/**
 * Creates a new V2 step of the specified kind with an auto-generated ID.
 */
export function buildV2Step(kind: StepKind, existingSteps: JsonOpcodeStep[]): JsonOpcodeStep {
    const id = generateStepId(existingSteps);

    switch (kind) {
        case 'llm': {
            const step = makeLLMStepTemplate();
            return {
 ...step,
id
};
        }
        case 'rag': {
            const step = makeRagBasicSearchTemplate();
            return {
 ...step,
id
};
        }
        case 'api': {
            const step = makeApiStepTemplate();
            return {
 ...step,
id
};
        }
        case 'memory': {
            const step = makeMemoryStepTemplate();
            return {
 ...step,
id
};
        }
        case 'choose': {
            const step = makeChooseStepTemplate();
            return {
 ...step,
id
};
        }
        case 'subflow': {
            const step = makeSubflowStepTemplate();
            return {
 ...step,
id
};
        }
        case 'tool': {
            const step = makeToolStepTemplate();
            return {
 ...step,
id
};
        }
        // A-0012.02: parallel + call factories — minimal valid shapes per
        // SCHEMAS.md §4. No templates.ts factories existed for these because
        // the legacy V2StepDispatcher only built them via direct user input.
        // Now that the new DS-themed inline editors (InlineParallelStep /
        // InlineCallStep) need to seed a fresh step, we add the templates here.
        case 'parallel': {
            return ({
                id,
                kind: 'parallel',
                branches: []
            } as unknown) as JsonOpcodeStep;
        }
        case 'call': {
            if (!CALL_STEP_AUTHORING_ENABLED) {
                throw new Error(
                    'call step authoring is disabled — Project O runtime does not register kind:"call" yet'
                );
            }
            return ({
                id,
                kind: 'call',
                call: {
 ref: ''
},
                inputs: {}
            } as unknown) as JsonOpcodeStep;
        }
        default:
            throw new Error(`Unknown step kind: ${kind}`);
    }
}

/**
 * Changes a step's kind while preserving its ID and any applicable controllers.
 */
export function changeStepKind(
    existingStep: JsonOpcodeStep,
    newKind: StepKind,
    existingSteps: JsonOpcodeStep[]
): JsonOpcodeStep {
    const newStep = buildV2Step(newKind, existingSteps);
    // A-0008.03: discriminated-union spread loses type narrowing in TS;
    // explicit cast keeps this back-compat helper compiling. Stage 3 will
    // replace this with a six-block-aware kind-changer that picks the
    // appropriate template factory directly.
    return {
        ...newStep,
        id: existingStep.id,
        // Preserve controllers if they were set
        ...(existingStep.when ? {
 when: existingStep.when
} : {}),
        ...(existingStep.for ? {
 for: existingStep.for
} : {}),
        ...(existingStep.repeat_until ? {
 repeat_until: existingStep.repeat_until
} : {}),
    } as JsonOpcodeStep;
}
